import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { ArrowRight, CircleCheck } from 'lucide-react';
import { get } from 'lodash';
import PropTypes from 'prop-types';

import useWorkflowGuard from 'hooks/useWorkflowGuard';
import { checkinAction, nextAction } from 'actions/workflow';
import { accountMask } from 'utils/masks';
import { FormColumn, FormRow, Input, Select } from 'components/Form';
import RoutingNumberInput from 'components/Form/RoutingNumberInput';
import { Button, Toggle } from 'components/Button';
import InfoTooltip from 'components/InfoTooltip';
import Validator from 'components/Validator';
import Spinner from 'components/Spinner';
import GeneralError from 'components/GeneralError';
import Alert from 'components/Alert';
import Plaid from 'components/Plaid';
import Modal from 'components/Modal';

import FundingAccountSchema from './schema';

const FundingAccount = ({
  navigate,
  searchParams,
  dispatch,
  validator: { errors, values, setValues, validate, deleteErrors, setErrors, onChangeHandler }
}) => {
  useWorkflowGuard(searchParams, navigate);
  const checkinData = useSelector((state) => get(state, 'workflow.data'));
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isPlaidVisible, setIsPlaidVisible] = useState(false);
  const [isBankAcountLoading, setIsBankAcountLoading] = useState(false);
  const [generalError, setGeneralError] = useState(null);
  const [hasBankStoredAccounts, setHasBankStoredAccounts] = useState(false);
  const [selectedAccount, setSelectedAccount] = useState(null);
  const [accounts, setAccounts] = useState([]);
  const [plaidErrorMessage, setPlaidErrorMessage] = useState(null);
  const [giactPopupAttrs, setGiactPopupAttrs] = useState(null);
  const [giactMessage, setGiactMessage] = useState(null);
  const [isGiactPopupVisible, setIsGiactPopupVisible] = useState(false);
  const qualifiesForPromo = get(checkinData, 'repaymentType') === 'Interest Only' && get(checkinData, 'hasCompletedPlaid');
  const isAutoPayEnabled = get(values, 'isAutopay');

  const handleBankAccounts = (data) => {
    const storedAccounts = get(data, 'accounts') || [];
    const newValues = {
      ...values,
      hasBankStoredAccounts: true,
      accountHolderName: `${get(checkinData, 'firstName')} ${get(checkinData, 'lastName')}`,
    };
    if (storedAccounts.length > 0) {
      const primaryAccount = storedAccounts.find((acc) => acc.isPrimary);
      if (primaryAccount) {
        setSelectedAccount(primaryAccount);
        newValues.selectedAccount = {
          label: `${primaryAccount.bankName} - ${primaryAccount.accountType} ${primaryAccount.accountNumber}`,
          values: primaryAccount.accountId,
        }
      }
      setValues(newValues);
      const newAccounts = storedAccounts.map((acc) => ({
        label: `${acc.bankName} - ${acc.accountType} ${acc.accountNumber}`,
        value: acc.accountId,
      }));
      newAccounts.push({ label: '+ Add New Bank Account', value: 'addNew' })
      setAccounts(newAccounts);
    }
  }

  useEffect(() => {
    const storedAccounts = get(checkinData, 'accounts') || [];
    if (checkinData && storedAccounts.length > 0) {
      setHasBankStoredAccounts(true);
      handleBankAccounts(checkinData);
    } else {
      setValues({
        ...values,
        hasBankStoredAccounts: false,
        accountHolderName: `${get(checkinData, 'firstName')} ${get(checkinData, 'lastName')}`,
      });
    }
  }, [checkinData]);

  const handleChange = (field, value) => {
    onChangeHandler(field, value);
  };

  const handleSelectedAccountChange = (value) => {
    const storedAccounts = get(checkinData, 'accounts') || [];
    const newAccount = storedAccounts.find((acc) => acc.accountId === get(value, 'value'));
    if (newAccount) {
      setSelectedAccount(newAccount);
    }
    onChangeHandler('selectedAccount', value);
  }

  const togglePlaid = () => {
    setIsPlaidVisible(true);
  };

  const handlePlaidBankAccount = (res, timerId) => {
    handleBankAccounts(get(res, 'data'));
    setIsBankAcountLoading(false);
    timerId && clearTimeout(timerId);
  };

  const handlePlaidSuccess = (publicToken, metadata, sessionId) => {
    const key = searchParams.get('key');
    if (key) {
      setIsPlaidVisible(false);
      setIsBankAcountLoading(true);
      dispatch(nextAction({
        key,
        data: {
          isPlaidComplete: true,
          plaidSessionId: sessionId,
          publicToken: {
            linkSessionId: get(metadata, 'link_session_id'),
            publicToken,
            accounts: (get(metadata, 'accounts') || []).map(acc => ({
              accountId: get(acc, 'id'),
              mask: get(acc, 'mask'),
              name: get(acc, 'name'),
              subtype: get(acc, 'subtype'),
              type: get(acc, 'type'),
            })),
            institution: {
              institutionId: get(metadata, 'institution.institution_id'),
              name: get(metadata, 'institution.name'),
            },
          },
        },
        success: (response) => {
          const routeUrl = get(response, 'data.url');
          if (get(response, 'data.isReady')) {
            navigate(routeUrl);
            handlePlaidBankAccount(response);
          } else {
            const timerId = setInterval(() => {
              if (get(response, 'data.isReady') === false) {
                dispatch(checkinAction({
                  key,
                  data: { checkStatus: true },
                  success: (response1) => {
                    if (get(response1, 'data.isReady')) {
                      handlePlaidBankAccount(response1, timerId);
                    }
                  },
                  fail: (err) => handleFail(err, true),
                }));
              } else if (get(response, 'data.isReady') === true) {
                handlePlaidBankAccount(response, timerId);
              }
            }, 5000);
          }
        },
        fail: (err) => handleFail(err, true),
      }));
    }
  };

  const handleRoutingNumberChange = (field, val) => {
    setValues({ ...values, [field]: val });
  };

  const handleRoutingNumberValidationSuccess = (field, validateData) => {
    setValues({
      ...values,
      [field]: validateData.routingNumber,
      [field === 'routingNumber' ? 'bankName' : 'autoBankName']: validateData.name,
      [field === 'routingNumber' ? 'isValidRoutingNumber' : 'isValidAutoRoutingNumber']: true,
    });
    deleteErrors([field]);
  };

  const handleRoutingNumberValidationError = (field, errorMessage, routingNumber) => {
    setErrors({ ...errors, [field]: errorMessage });
    setValues({
      ...values,
      [field]: routingNumber,
      [field === 'routingNumber' ? 'bankName' : 'autoBankName']: '',
      [field === 'routingNumber' ? 'isValidRoutingNumber' : 'isValidAutoRoutingNumber']: false,
    });
  };

  const handlePlaidExit = () => {
    setIsPlaidVisible(false);
  };

  const toggleGiactPopup = (e) => {
    e && e.preventDefault();
    setIsGiactPopupVisible(!isGiactPopupVisible);
  };

  const handleFail = (err, isPlaid = false) => {
    const errorMessage = get(err, 'data.failure.errors.0.message');
    const subject = get(err, 'data.failure.errors.0.subject', '');

    if (subject === 'bankAccountVerificationFailed') {
      setIsGiactPopupVisible(true);
      setGiactPopupAttrs(get(err, 'data.failure.errors.0.attributes'));
      setGiactMessage(errorMessage);
    } else if (errorMessage) {
      if (isPlaid) {
        setPlaidErrorMessage(errorMessage);
      } else {
        setGeneralError(get(err, 'data.failure.errors.0'));
      }
    }
    setIsSubmitting(false);
  }

  const handleNext = (e) => {
    e.preventDefault();
    setGeneralError(null);

    if (!validate().isValid) return;

    setIsSubmitting(true);
    const data = {
      isAutoPay: get(values, 'isAutopay', false),
    }

    if (!hasBankStoredAccounts || get(values, 'selectedAccount.value') === 'addNew') {
      data.fundingAccount = {
        accountNumber: get(values, 'accountNumber'),
        routingNumber: get(values, 'routingNumber'),
        accountType: get(values, 'accountType.label'),
        accountHolderName: get(values, 'accountHolderName'),
        bankName: get(values, 'bankName'),
      }
    } else {
      data.fundingAccountId = get(selectedAccount, 'accountId');
    }

    dispatch(nextAction({
      key: searchParams.get('key'),
      data,
      success: (res) => {
        navigate(get(res, 'data.url'));
        setIsSubmitting(false);
      },
      fail: handleFail,
    }));
  };

  return (
    <div className="flex flex-col mb-2 mt-10 px-4">
      <h2 className="font-semibold mb-5">Funding Account Details</h2>

      {isPlaidVisible ? (
        <div className="mb-4">
          <Plaid
            onPlaidSuccess={handlePlaidSuccess}
            onPlaidExit={handlePlaidExit}
          />
        </div>
      ) : isBankAcountLoading ? (
        <div className="flex justify-center items-center h-16 mb-4">
          <Spinner size={8} color="blue-400" />
        </div>
      ) : (
        <div className="w-full mb-5">
          {hasBankStoredAccounts ? (
            <FormRow>
              <FormColumn>
                <Select
                  label="Select Account"
                  required
                  options={accounts}
                  value={values.selectedAccount}
                  onChange={handleSelectedAccountChange}
                  error={errors.selectedAccount}
                  placeholder="Select an account"
                />
              </FormColumn>
            </FormRow>
          ) : null}

          {!hasBankStoredAccounts || get(values, 'selectedAccount.value') === 'addNew' ? (
            <>
              <FormRow>
                <FormColumn>
                  <Input
                    label="Name on Account"
                    value={values.accountHolderName}
                    error={errors.accountHolderName}
                    onChange={(e) => handleChange('accountHolderName', e.target.value)}
                    required
                  />
                </FormColumn>
                <FormColumn>
                  <Select
                    required
                    label="Account Type"
                    options={[
                      { label: 'Checking', value: 'checking' },
                      { label: 'Savings', value: 'savings' },
                    ]}
                    value={values.accountType}
                    error={errors.accountType}
                    onChange={(val) => handleChange('accountType', val)}
                  />
                </FormColumn>
              </FormRow>
              <FormRow>
                <FormColumn>
                  <RoutingNumberInput
                    value={values.routingNumber}
                    error={errors.routingNumber}
                    bankName={values.bankName}
                    onChange={(val) => handleRoutingNumberChange('routingNumber', val)}
                    onValidationSuccess={(data) => handleRoutingNumberValidationSuccess('routingNumber', data)}
                    onValidationError={(msg, routingNumber) => handleRoutingNumberValidationError('routingNumber', msg, routingNumber)}
                  />
                </FormColumn>
                <FormColumn>
                  <Input
                    required
                    label="Account Number"
                    value={values.accountNumber}
                    error={errors.accountNumber}
                    mask={accountMask}
                    inputMode="numeric"
                    onChange={(e) => handleChange('accountNumber', e.target.value)}
                  />
                </FormColumn>
              </FormRow>

              <FormRow>
                <span className="text-sm text-slate-600 underline cursor-pointer" onClick={togglePlaid}>Click here to securely link your bank account for funding.</span>
              </FormRow>

              {plaidErrorMessage ? (
                <FormRow className="w-full mb-4">
                  <FormColumn>
                    <Alert variant="danger">
                      <p className='text-sm'>
                        {plaidErrorMessage}
                      </p>
                    </Alert>
                  </FormColumn>
                </FormRow>
              ) : null}
            </>
          ) : null}
        </div>
      )}

      {hasBankStoredAccounts && get(values, 'selectedAccount.value') !== 'addNew' ? (
        <div className="flex flex-wrap mb-4 gap-y-4 md:gap-y-0">
          <div className="flex flex-col gap-2 py-3 px-4 md:px-6 md:pl-0 border-l md:border-0 md:border-r border-gray-200 w-1/2 md:w-3/12">
            <span className="font-light uppercase text-xs text-slate-800">Name on Account</span>
            <h5 className="text-sm font-semibold">{get(selectedAccount, 'accountHolderName')}</h5>
          </div>
          <div className="flex flex-col gap-2 py-3 px-4 md:px-6 border-l md:border-0 md:border-r border-gray-200 w-1/2 md:w-3/12">
            <span className="font-light uppercase text-xs text-slate-800">Account Type</span>
            <h5 className="text-sm font-semibold">{get(selectedAccount, 'accountType')}</h5>
          </div>
          <div className="flex flex-col gap-2 py-3 px-4 md:px-6 border-l md:border-0 md:border-r border-gray-200 w-1/2 md:w-3/12">
            <span className="font-light uppercase text-xs text-slate-800">Routing Number</span>
            <h5 className="text-sm font-semibold">{get(selectedAccount, 'routingNumber')}</h5>
            <span className="text-xs text-blue-500 font-normal">{get(selectedAccount, 'bankName')}</span>
          </div>
          <div className="flex flex-col gap-2 py-3 px-4 md:pl-6 w-1/2 border-l md:border-0 border-gray-200 md:w-3/12">
            <span className="font-light uppercase text-xs text-slate-800">Account Number</span>
            <h5 className="text-sm font-semibold">{get(selectedAccount, 'maskedAccountNumber') || get(selectedAccount, 'accountNumber')}</h5>
          </div>
        </div>
      ) : null}

      <div className="flex flex-col gap-6 w-full my-8">
        <div className={`flex flex-col gap-1 rounded-lg border ${isAutoPayEnabled ? 'border-green-500 bg-green-100' : 'border-blue-500 bg-blue-100'} p-4`}>
          <div className="flex items-center justify-between gap-4 flex-wrap">
            <div className="flex items-center gap-3 flex-wrap">
              <h2 className={`font-semibold ${isAutoPayEnabled ? 'text-green-500' : 'text-blue-500'}`}>Auto-Pay</h2>
              <InfoTooltip
                title="What is Auto-Pay?"
                tooltip={(
                  <div className="md:w-[300px] whitespace-normal my-2">
                    <p className="text-sm mb-4">Auto-Pay automatically deducts your scheduled loan payments from your linked funding account on the due date.</p>

                    <h5 className="font-semibold mb-2">Benefits</h5>
                    <ul className="list-disc pl-6">
                      {qualifiesForPromo ? (
                        <li>
                          <span className="font-semibold">Earn a 0.25% interest rate discount</span>
                          {' '}
                          when you enroll in Auto-Pay if you also verified your bank account only through our instant verification system. Please note that you may lose your 0.25% promotional interest rate reduction if you unenroll from Auto-Pay during your loan term. The 0.25% promotional interest rate reduction is
                          {' '}
                          <span className="underline">not available</span>
                          {' '}
                          for the "No Monthly Payment" repayment option.
                        </li>
                      ) : null}
                      <li>
                        <span className="font-semibold">Avoid missed payments</span>
                        {' '}
                        – Payments are made automatically, reducing the chance of late fees or collateral liquidation. 
                      </li>
                    </ul>
                  </div>
                )}
                hoverColor="blue-700"
              />
              <span className={`px-2 py-1 leading-none text-xs bg-white rounded-lg border ${isAutoPayEnabled ? 'text-green-500 border-green-500' : 'text-blue-500 border-blue-500'}`}>
                Recommended
              </span>
            </div>
            <div className="flex items-center gap-2">
              <span className="text-sm text-slate-500">Enroll</span>
              <Toggle color="green" checked={values.isAutopay} onChange={(val) => {
                setValues({
                  ...values,
                  isAutopay: val,
                });
              }} />
            </div>
          </div>
          {qualifiesForPromo ? (
            <>
              <p className={`${isAutoPayEnabled ? 'text-green-500' : 'text-blue-500'} text-sm`}>
                {isAutoPayEnabled ? 'Promotional rate secured' : 'Keep your 0.25% rate discount'}
              </p>
              <div className="flex items-center justify-center border text-sm bg-white border-gray-300 rounded-lg p-4 mt-4 overflow-hidden">
                {isAutoPayEnabled ? (
                  <div className="flex items-center gap-2 animate-fade-in">
                    <CircleCheck className="h-5 w-5 text-green-500" />
                    <span className="font-semibold text-green-800">{get(checkinData, 'autoPayInterestRate')}% interest rate</span>
                  </div>
                ) : (
                  <div className="flex items-center justify-between w-full">
                    <div className="flex flex-col gap-2 font-semibold text-gray-600">
                      Without AutoPay
                      <span className="text-blue-900">{get(checkinData, 'nonAutoPayInterestRate')}% interest rate</span>
                    </div>
                    <ArrowRight className="text-gray-600 h-5 w-5" />
                    <div className="flex flex-col gap-2 font-semibold text-gray-600 text-right">
                      With AutoPay
                      <span className="text-blue-900">{get(checkinData, 'autoPayInterestRate')}% interest rate</span>
                    </div>
                  </div>
                )}
              </div>
            </>
          ) : null}
        </div>
      </div>

      {isGiactPopupVisible && (
        <Modal title="Unable to Verify Account" onClose={toggleGiactPopup}>
          <div className="flex flex-col gap-4">
            <p className="text-slate-600 text-center mb-2">
              {giactMessage}
            </p>
            {
              giactPopupAttrs && giactPopupAttrs.allowRetry && (
                <Button
                  className="w-100"
                  onClick={toggleGiactPopup}
                  modifier="danger"
                  label="Try Again"
                />
              )
            }
          </div>
        </Modal>
      )}

      <GeneralError error={generalError} className="w-full mb-4" />

      <FormRow>
        <Button
          className="w-full"
          label="Next Step: Collateral Deposit"
          onClick={handleNext}
          isLoading={isSubmitting}
        />
      </FormRow>

      {get(checkinData, 'repaymentType') !== 'No Payment' ? (
        <FormRow className="border-t border-gray-200 pt-6 mt-4 !mb-0">
          <p className="text-xs text-gray-500 leading-relaxed pr-4">
            <span className="font-semibold text-gray-600">Please Note: </span>
            Enrolling in Auto-Pay is optional. The Bank's credit decision is not dependent on whether you enroll in Auto-Pay. You can change your method of payment at any time in the future.
          </p>
        </FormRow>
      ) : null}
    </div>
  );
};

FundingAccount.propTypes = {
  navigate: PropTypes.func.isRequired,
  dispatch: PropTypes.func.isRequired,
  searchParams: PropTypes.object.isRequired,
  validator: PropTypes.object.isRequired,
};

export default Validator(FundingAccountSchema)(FundingAccount);
